import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, LogOut, User, ChevronDown, UserPlus, LogIn, MoreVertical } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NavbarMobile = ({ 
  navItems, 
  activeSection, 
  scrollTo, 
  renderIcon, 
  renderLogo, 
  isLoggedIn, 
  userName, 
  imgSrc, 
  handleLogout 
}) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // State untuk kontrol manual

  return (
    <div className="xl:hidden">
      {/* --- MOBILE HEADER --- */}
      <div className="fixed top-0 left-0 w-full h-24 bg-white/80 dark:bg-slate-950/90 backdrop-blur-xl border-b border-slate-100 flex items-center px-6 z-50">
        {renderLogo()}
      </div>

      {/* --- APPLE-STYLE FLOATING BUTTON (TRIGGER) --- */}
      {/* Bulatan ini akan tetap terlihat sebagai pemicu */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={`fixed left-4 top-80 z-[60] w-12 h-12 rounded-full bg-white/20 dark:bg-slate-800/20 backdrop-blur-md border border-white/30 dark:border-slate-700/30 shadow-xl flex items-center justify-center transition-all duration-300 active:scale-90 ${
          isSidebarOpen ? "opacity-0 invisible scale-0" : "opacity-100 visible scale-100"
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-slate-800/40 dark:bg-white/40 flex items-center justify-center">
          <div className="w-4 h-4 rounded-full bg-white dark:bg-slate-900 shadow-inner" />
        </div>
      </button>

      {/* --- SIDEBAR AREA --- */}
      <div 
        className={`fixed left-0 top-0 z-50 h-screen transition-all duration-500 ${
          isSidebarOpen ? "w-screen bg-slate-900/20 backdrop-blur-sm" : "w-0"
        }`}
        onClick={() => setIsSidebarOpen(false)} // Klik di luar untuk tutup
      >
        <div 
          onClick={(e) => e.stopPropagation()} // Cegah tutup saat klik di dalam sidebar
          className={`
            relative ml-2 mt-32 w-[115px] h-auto flex flex-col items-stretch 
            bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl 
            border border-slate-200/50 dark:border-slate-800 
            shadow-2xl rounded-[2.5rem] py-6 px-0 overflow-visible
            transition-all duration-500 transform 
            ${isSidebarOpen ? "translate-x-0 opacity-100" : "-translate-x-[130%] opacity-0"}
          `}
        >
          {/* Tombol Tutup Internal (Opsional) */}
          <button 
            onClick={() => setIsSidebarOpen(false)}
            className="absolute -right-2 -top-2 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-[10px] border-2 border-white shadow-lg"
          >
            ✕
          </button>

          {/* MENU UTAMA */}
          <div className="flex flex-col gap-6 mb-8 items-center px-2">
            {navItems.map((item) => (
              <div
                key={item.id}
                onClick={() => {
                  scrollTo(item.id);
                  setIsSidebarOpen(false);
                }}
                className="group/item relative cursor-pointer flex items-center justify-center p-2 rounded-xl transition-all hover:bg-blue-50 dark:hover:bg-slate-800"
              >
                <div className={`${activeSection === item.id ? "text-blue-600" : "text-slate-400 dark:text-slate-300"}`}>
                   {renderIcon(item.iconName)}
                </div>
              </div>
            ))}
          </div>

          {/* --- PROFILE / AUTH SECTION --- */}
          <div className="flex flex-col w-full mt-auto px-2 border-t border-slate-100 dark:border-slate-800 pt-5 pb-4">
            {isLoggedIn ? (
              <div className="flex flex-col items-center w-full">
                <div 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="relative cursor-pointer flex flex-col items-center gap-1"
                >
                  <div className={`h-12 w-12 rounded-full overflow-hidden border-2 transition-all ${
                      showUserMenu ? 'border-blue-500' : 'border-slate-100 dark:border-slate-700'
                    }`}>
                    {imgSrc ? (
                      <img src={imgSrc} alt="User" className="h-full w-full object-cover rounded-full" />
                    ) : (
                      <div className="h-full w-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center rounded-full">
                        <User size={20} className="text-slate-400" />
                      </div>
                    )}
                  </div>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform ${showUserMenu ? "rotate-180" : ""}`} />
                </div>

                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden w-full flex flex-col gap-1 mt-3"
                    >
                      <div className="py-1 px-1 text-center border-b border-slate-50 dark:border-slate-800 mb-2">
                        <p className="text-[9px] font-black text-slate-800 dark:text-white uppercase tracking-tight">{userName}</p>
                      </div>
                      <div onClick={() => navigate("/settings")} className="cursor-pointer flex items-center justify-center p-2 text-slate-500 hover:text-blue-600"><Settings size={14} /></div>
                      <div onClick={handleLogout} className="cursor-pointer flex items-center justify-center p-2 text-red-500"><LogOut size={14} /></div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div onClick={() => navigate("/register")} className="cursor-pointer flex items-center justify-center p-3 rounded-xl bg-blue-600 shadow-lg active:scale-95 transition-all">
                  <UserPlus size={18} className="text-white" />
                </div>
                <div onClick={() => navigate("/login")} className="cursor-pointer flex items-center justify-center p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 active:scale-95 transition-all shadow-sm">
                  <LogIn size={18} className="text-slate-600 dark:text-slate-300" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavbarMobile;